import { type BoReference } from '../bos/bo-reference.js'
import { type BusinessObject } from '../bos/business-object.js'
import type {
	BusinessObjectTypeType,
	ModuleIdType,
	NameAndType,
} from '../types.js'

import { TypeIcons, PrimitiveConstructorNames } from '../consts.js'
const zodTypeExceptions = {
	Date: 'z.coerce.date()',
	ArrayBuffer: 'z.instanceof(ArrayBuffer)',
	Buffer: 'z.instanceof(Buffer)',
	BigInt: 'z.bigint()',
	Uint8Array: 'z.instanceof(Uint8Array)',
	Int8Array: 'z.instanceof(Int8Array)',
	Uint16Array: 'z.instanceof(Uint16Array)',
	Int16Array: 'z.instanceof(Int16Array)',
	Uint32Array: 'z.instanceof(Uint32Array)',
	Int32Array: 'z.instanceof(Int32Array)',
	Float32Array: 'z.instanceof(Float32Array)',
	Float64Array: 'z.instanceof(Float64Array)',
	BigInt64Array: 'z.instanceof(BigInt64Array)',
	BigUint64Array: 'z.instanceof(BigUint64Array)',
	Uint8ClampedArray: 'z.instanceof(Uint8ClampedArray)',
	Array: 'z.instanceof(Array)',
	Object: 'z.instanceof(Object)',
}

export type BoAlternativeTypeLookupFn = (
	boTypeRef: BoType,
) => TypeReference | null
export class TypeReference {
	private type: AbstractType

	constructor(
		init?: BoReference | string | AbstractType,
		boType?: BusinessObjectTypeType,
		suffixes?: string[],
	) {
		if (init instanceof AbstractType) {
			if (boType || suffixes?.length) {
				throw new Error(
					'BoType and suffixes are not allowed when initializing a TypeReference from an AbstractType',
				)
			}
			this.type = init
			return
		}

		if (typeof init == 'string') {
			if (suffixes?.length) {
				throw new Error(
					'Suffixes are not allowed when initializing a TypeReference from a string',
				)
			}
			this.type = TypeReference.parseToTypeClass(init, boType)
			// 	init = init.trim()
			// 	this.kind = 'primitive'

			// 	const isPromiseMatch = init.match(/^Promise<(.*)>$/)
			// 	if (isPromiseMatch) {
			// 		this.isPromise = true
			// 		init = isPromiseMatch[1]
			// 	}

			// 	this.isArray = init.endsWith('[]')
			// 	init = this.isArray ? init.substring(0, init.length - 2) : init

			// 	this.typeName = init

			// 	const [primitiveOrModuleId, boId, suffix, ...rest] = init.split('.')
			// 	if (suffix == 'Id') {
			// 		this.kind = 'type'
			// 		this.boModuleId = primitiveOrModuleId
			// 		this.boId = boId
			// 		this.suffix = suffix
			// 		this.typeName = `${boId}θ${suffix}`
			// 	} else if (init.startsWith('Sys.Types.')) {
			// 		// no special treatment; treat as primitive
			// 	} else if (boId) {
			// 		if (primitiveOrModuleId != 'Temporal') {
			// 			this.kind = 'bo'
			// 			this.boModuleId = primitiveOrModuleId
			// 			this.boId = boId
			// 			this.suffix = suffix
			// 		}
			// 	}
		} else if (init) {
			this.type = new BoType(boType || init.boType, [
				init.moduleId,
				init.boId,
				...suffixes,
			])
		} else {
			throw new Error('Invalid type reference')
		}
	}

	private static splitTopLevel(input: string, delimiter: string): string[] {
		const parts: string[] = []
		let depth = 0
		let current = ''

		for (const char of input) {
			if ('{[(<'.includes(char)) depth++
			else if ('}])>'.includes(char)) depth--

			if (char === delimiter && depth === 0) {
				parts.push(current)
				current = ''
			} else {
				current += char
			}
		}
		parts.push(current)
		return parts
	}
	private static parseToTypeClass(
		typeStr: string,
		boType?: BusinessObjectTypeType,
	): AbstractType {
		typeStr = typeStr.trim()

		if (boType) {
			return new BoType(boType, typeStr.split('.'))
		}

		const promiseMatch = typeStr.match(/^Promise<(.*)>$/)
		if (promiseMatch) {
			return new PromiseType(this.parseToTypeClass(promiseMatch[1]))
		}

		const arrayMatch =
			typeStr.match(/^Array<(.*)>$/) || typeStr.match(/^(.*)\[\]$/)
		if (arrayMatch) {
			return new ArrayType(this.parseToTypeClass(arrayMatch[1]))
		}

		const tupleMatch = typeStr.match(/^\[(.*)\]$/)
		if (tupleMatch) {
			return new TupleType(
				this.splitTopLevel(tupleMatch[1], ',').map((type) =>
					this.parseToTypeClass(type.trim()),
				),
			)
		}

		const setMatch = typeStr.match(/^Set<(.*)>$/)
		if (setMatch) {
			return new SetType(this.parseToTypeClass(setMatch[1]))
		}

		const recordMatch = typeStr.match(/^Record<(.*)>$/)
		if (recordMatch) {
			const [keyType, valueType] = this.splitTopLevel(recordMatch[1], ',')
			return new RecordType(
				this.parseToTypeClass(keyType.trim()),
				this.parseToTypeClass(valueType.trim()),
			)
		}

		const objectMatch = typeStr.match(/^\{(.*)\}$/)
		if (objectMatch) {
			const propsStr = objectMatch[1].trim()
			const props = propsStr === '' ? [] : this.splitTopLevel(propsStr, ',')

			return new ObjectType(
				new Map(
					props.map((prop) => {
						const [key, ...rest] = this.splitTopLevel(prop, ':')
						return [key.trim(), this.parseToTypeClass(rest.join(':').trim())]
					}),
				),
			)
		}

		const paranthesesMatch = typeStr.match(/^\((.*)\)$/)
		if (paranthesesMatch) {
			return this.parseToTypeClass(paranthesesMatch[1])
		}

		const boMatch = typeStr.match(/^([^:]*:)?([^.]*\..*)$/)
		if (boMatch) {
			const boType = boMatch[1] as BusinessObjectTypeType
			const parts = boMatch[2].split('.')
			return new BoType(boType, parts)
		}

		const unionMatch = typeStr.match(/^([^|]+\|.+)$/)
		if (unionMatch) {
			return new UnionType(
				unionMatch[1].split('|').map((type) => this.parseToTypeClass(type)),
			)
		}

		return new PrimitiveType(typeStr)
	}

	getDisplayText() {
		// let icon = '[?]'

		// switch (this.kind) {
		// 	case 'primitive':
		// 		icon = TypeIcons[this.typeName as keyof typeof TypeIcons]
		// 		break

		// 	case 'type':
		// 		icon = '[T]'
		// 		break

		// 	case 'bo':
		// 		icon = TypeIcons[this.boType as keyof typeof TypeIcons]
		// 		break

		// 	default:
		// 		console.log(
		// 			`Error: icon for type reference kind ${this.kind} is not implemented`,
		// 		)
		// }

		// return `${icon} ${this.getQualifiedSimpleTypeName()}`
		return this.type.getDisplayText()
	}

	getQualifiedSimpleTypeName() {
		// let type: string

		// switch (this.kind) {
		// 	case 'primitive':
		// 		type = this.typeName
		// 		break

		// 	case 'type':
		// 		type = (this.boModuleId ? `${this.boModuleId}.` : '') + this.typeName
		// 		break

		// 	case 'bo':
		// 		type =
		// 			`${this.boModuleId}.${this.boId}` +
		// 			(this.suffix ? `.${this.suffix}` : '')
		// 		break
		// }

		// if (type === undefined) console.error(`Type unknown for kind ${this.kind}`)
		// return type
		return this.type.getQualifiedTypeName()
	}

	getQualifiedTypeName(promisify = false) {
		// let type = this.getQualifiedSimpleTypeName()
		// type = this.isArray ? `${type}[]` : type
		// if (this.isPromise || promisify) type = `Promise<${type}>`

		// return type
		const type = promisify ? new PromiseType(this.type) : this.type
		return type.getQualifiedTypeName()
	}

	getConstructorName(addArrayIfApplicable = false) {
		// let name: string
		// if (this.kind == 'primitive') {
		// 	name =
		// 		PrimitiveConstructorNames[
		// 			this.typeName as keyof typeof PrimitiveConstructorNames
		// 		]
		// } else {
		// 	name = this.getQualifiedSimpleTypeName()
		// }
		// if (addArrayIfApplicable && this.isArray) name += '[]'
		// return name
		return this.type.getConstructorName(addArrayIfApplicable)
	}

	getZodType(boAlternativeTypeLookup: BoAlternativeTypeLookupFn): string {
		// let type: string
		// switch (this.kind) {
		// 	case 'primitive':
		// 		if (this.typeName in zodTypeExceptions)
		// 			type =
		// 				zodTypeExceptions[this.typeName as keyof typeof zodTypeExceptions]
		// 		else type = `z.${this.typeName}()`
		// 		break
		// 	case 'type':
		// 		if (this.boModuleId && this.boId) {
		// 			type = `${this.boModuleId}.${this.boId}.$getSchema().shape.${this.suffix.toLowerCase()}`
		// 		} else {
		// 			type = this.typeName
		// 		}
		// 		break
		// 	case 'bo':
		// 		type = `${this.boModuleId}.${this.boId}${this.suffix ? `.${this.suffix}` : ''}.$getSchema()`
		// 		break
		// }
		// if (this.isArray) type += '.array()'
		// return type
		return this.type.getZodType(boAlternativeTypeLookup)
	}

	forModule(
		contextModule: ModuleIdType | BusinessObject | BoReference,
	): TypeReference {
		// if (this.kind != 'bo' && this.kind != 'type') return this
		// if (this.boModuleId != 'This') return this
		if (typeof contextModule != 'string' && 'moduleId' in contextModule)
			contextModule = contextModule.moduleId

		// return new TypeReference({
		// 	...this,
		// 	boModuleId: contextModule,
		// })
		return new TypeReference(this.type.toModule(contextModule))
	}

	static toNameAndType(name: string, type: string): NameAndType {
		const isArray = type.endsWith('[]') ? true : undefined
		if (isArray) type = type.substring(0, type.length - 2)
		return { name, type, isArray }
	}

	static toNameAndTypeForModule(
		name: string,
		type: string,
		contextModuleId: string,
	): NameAndType {
		type = type?.replaceAll('This.', contextModuleId + '.')
		return this.toNameAndType(name, type)
	}

	static adaptTypeForThisModule(type: string, contextModuleId: string): string {
		type = type?.replaceAll(contextModuleId + '.', 'This.')
		return type
	}

	get kind() {
		return this.type.kind
	}
	get boType(): BusinessObjectTypeType {
		return this.type.boType
	}
	get boModuleId(): string {
		return this.type.boModuleId
	}
	get boId(): string {
		return this.type.boId
	}
	get suffixes(): string[] {
		return this.type.suffixes
	}
	get isArrayOrSet(): boolean {
		return this.type.isArrayOrSet
	}
}

abstract class AbstractType {
	abstract get kind(): 'primitive' | 'bo' | 'type'
	abstract getDisplayText(): string
	abstract getQualifiedTypeName(): string
	abstract getZodType(
		boAlternativeTypeLookup: BoAlternativeTypeLookupFn,
	): string
	getQualifiedSimpleTypeName(): string {
		return this.getQualifiedTypeName()
	}
	getConstructorName(_addArrayIfApplicable = false) {
		return this.getQualifiedSimpleTypeName()
	}
	toModule(_moduleId: string): AbstractType {
		return this
	}
	get boType(): BusinessObjectTypeType {
		console.warn(
			`Reference to ${this.getQualifiedTypeName()} does not have a boType`,
		)
		return undefined
	}
	get boModuleId(): string {
		console.warn(
			`Reference to ${this.getQualifiedTypeName()} does not have a moduleId`,
		)
		return undefined
	}
	get boId(): string {
		console.warn(
			`Reference to ${this.getQualifiedTypeName()} does not have a boId`,
		)
		return undefined
	}
	get suffixes(): string[] {
		console.warn(
			`Reference to ${this.getQualifiedTypeName()} does not have suffixes`,
		)
		return []
	}
	get isArrayOrSet(): boolean {
		return false
	}
}

class PrimitiveType extends AbstractType {
	readonly kind = 'primitive'
	constructor(public typeName: string) {
		super()
	}

	getDisplayText(): string {
		const icon = TypeIcons[this.typeName as keyof typeof TypeIcons]
		return `${icon} ${this.typeName}`
	}

	getQualifiedTypeName(): string {
		return this.typeName
	}

	getConstructorName() {
		return PrimitiveConstructorNames[
			this.typeName as keyof typeof PrimitiveConstructorNames
		]
	}

	getZodType(): string {
		if (this.typeName in zodTypeExceptions)
			return zodTypeExceptions[this.typeName as keyof typeof zodTypeExceptions]
		else return `z.${this.typeName}()`
	}
}

class BoType extends AbstractType {
	readonly kind = 'bo'
	private _boType: BusinessObjectTypeType
	constructor(
		boType: BusinessObjectTypeType,
		public parts: string[],
	) {
		super()
		this._boType = boType
	}

	getDisplayText(): string {
		const icon = TypeIcons[this.boType as keyof typeof TypeIcons]
		return `${icon} ${this.getQualifiedSimpleTypeName()}`
	}

	getQualifiedTypeName(): string {
		return this.parts.join('.')
	}

	getZodType(boAlternativeTypeLookup: BoAlternativeTypeLookupFn): string {
		const alternativeType = boAlternativeTypeLookup(this)
		if (alternativeType) {
			return alternativeType.getZodType(boAlternativeTypeLookup)
		}
		return `${this.parts.join('.')}.$getSchema()`
	}

	toModule(moduleId: string): AbstractType {
		if (this.parts[0] != 'This') return this
		return new BoType(this.boType, [moduleId, ...this.parts.slice(1)])
	}
	get boType(): BusinessObjectTypeType {
		return this._boType
	}
	get boModuleId(): string {
		return this.parts[0]
	}
	get boId(): string {
		return this.parts[1]
	}
	get suffixes(): string[] {
		return this.parts.slice(2)
	}
}

class ArrayType extends AbstractType {
	get kind() {
		return this.itemType.kind
	}
	constructor(public itemType: AbstractType) {
		super()
	}

	getDisplayText(): string {
		return this.getQualifiedTypeName()
	}

	getQualifiedTypeName(): string {
		return `${this.itemType.getQualifiedTypeName()}[]`
	}

	getQualifiedSimpleTypeName(): string {
		return this.itemType.getQualifiedSimpleTypeName()
	}

	getConstructorName(addArrayIfApplicable = false) {
		const name = this.itemType.getConstructorName()
		return addArrayIfApplicable ? `${name}[]` : name
	}

	get boType(): BusinessObjectTypeType {
		return this.itemType.boType
	}
	get boModuleId(): string {
		return this.itemType.boModuleId
	}
	get boId(): string {
		return this.itemType.boId
	}
	get suffixes(): string[] {
		return this.itemType.suffixes
	}

	getZodType(boAlternativeTypeLookup: BoAlternativeTypeLookupFn): string {
		return `z.array(${this.itemType.getZodType(boAlternativeTypeLookup)})`
	}

	toModule(moduleId: string): AbstractType {
		return new ArrayType(this.itemType.toModule(moduleId))
	}
	get isArrayOrSet(): boolean {
		return true
	}
}
class TupleType extends AbstractType {
	readonly kind = 'type'
	constructor(public itemTypes: AbstractType[]) {
		super()
	}

	getDisplayText(): string {
		return this.getQualifiedTypeName()
	}

	getQualifiedTypeName(): string {
		return `[${this.itemTypes.map((type) => type.getQualifiedTypeName()).join(', ')}]`
	}

	getZodType(boAlternativeTypeLookup: BoAlternativeTypeLookupFn): string {
		return `z.tuple([${this.itemTypes.map((type) => type.getZodType(boAlternativeTypeLookup)).join(', ')}])`
	}

	getConstructorName(): string {
		const qualifiedNames = this.itemTypes.map((type) =>
			type.getQualifiedSimpleTypeName(),
		)
		if (new Set(qualifiedNames).size == 1) {
			return this.itemTypes[0].getConstructorName()
		}
		return ''
	}

	toModule(moduleId: string): AbstractType {
		return new TupleType(this.itemTypes.map((type) => type.toModule(moduleId)))
	}
}
class SetType extends ArrayType {
	getZodType(boAlternativeTypeLookup: BoAlternativeTypeLookupFn): string {
		return `z.set(${this.itemType.getZodType(boAlternativeTypeLookup)})`
	}

	toModule(moduleId: string): AbstractType {
		return new SetType(this.itemType.toModule(moduleId))
	}
}

class RecordType extends AbstractType {
	readonly kind = 'type'
	constructor(
		public keyType: AbstractType,
		public valueType: AbstractType,
	) {
		super()
	}

	getDisplayText(): string {
		return this.getQualifiedTypeName()
	}

	getQualifiedTypeName(): string {
		return `Record<${this.keyType.getQualifiedTypeName()}, ${this.valueType.getQualifiedTypeName()}>`
	}

	getZodType(boAlternativeTypeLookup: BoAlternativeTypeLookupFn): string {
		return `z.record(${this.keyType.getZodType(boAlternativeTypeLookup)}, ${this.valueType.getZodType(boAlternativeTypeLookup)})`
	}

	getConstructorName(): string {
		return ''
	}

	toModule(moduleId: string): AbstractType {
		return new RecordType(
			this.keyType.toModule(moduleId),
			this.valueType.toModule(moduleId),
		)
	}
}

class ObjectType extends AbstractType {
	readonly kind = 'type'
	constructor(public properties: Map<string, AbstractType>) {
		super()
	}

	getDisplayText(): string {
		return this.getQualifiedTypeName()
	}
	getQualifiedTypeName(): string {
		return `{ ${Array.from(this.properties.entries())
			.map(([key, value]) => `${key}: ${value.getQualifiedTypeName()}`)
			.join(', ')} }`
	}

	getZodType(boAlternativeTypeLookup: BoAlternativeTypeLookupFn): string {
		return `z.object(${Array.from(this.properties.entries())
			.map(
				([key, value]) =>
					`${key}: ${value.getZodType(boAlternativeTypeLookup)}`,
			)
			.join(', ')})`
	}
	getConstructorName(): string {
		return ''
	}
	toModule(moduleId: string): AbstractType {
		return new ObjectType(
			new Map(
				Array.from(this.properties.entries()).map(([key, value]) => [
					key,
					value.toModule(moduleId),
				]),
			),
		)
	}
}

class UnionType extends AbstractType {
	readonly kind = 'type'
	constructor(public types: AbstractType[]) {
		super()
	}

	getDisplayText(): string {
		return `[T] ${this.getQualifiedTypeName()}`
	}
	getQualifiedTypeName(): string {
		return this.types.map((type) => type.getQualifiedTypeName()).join(' | ')
	}

	getZodType(boAlternativeTypeLookup: BoAlternativeTypeLookupFn): string {
		return `z.union(${this.types.map((type) => type.getZodType(boAlternativeTypeLookup)).join(', ')})`
	}

	getConstructorName(_addArrayIfApplicable?: boolean): string {
		return ''
	}

	toModule(moduleId: string): AbstractType {
		return new UnionType(this.types.map((type) => type.toModule(moduleId)))
	}
}

class PromiseType extends AbstractType {
	get kind() {
		return this.innerType.kind
	}
	constructor(public innerType: AbstractType) {
		super()
		if (this.innerType instanceof PromiseType) {
			return this.innerType
		}
	}

	getDisplayText(): string {
		return `[T] ${this.innerType.getQualifiedTypeName()}`
	}

	getQualifiedTypeName(): string {
		return `Promise<${this.innerType.getQualifiedTypeName()}>`
	}
	getSimpleTypeName(): string {
		return this.innerType.getQualifiedSimpleTypeName()
	}
	getZodType(boAlternativeTypeLookup: BoAlternativeTypeLookupFn): string {
		return `z.promise(${this.innerType.getZodType(boAlternativeTypeLookup)})`
	}
	getConstructorName(addArrayIfApplicable?: boolean): string {
		return this.innerType.getConstructorName(addArrayIfApplicable)
	}
	toModule(moduleId: string): AbstractType {
		return new PromiseType(this.innerType.toModule(moduleId))
	}
}
